import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from '../../../api';
import { toast } from 'react-toastify';
import i18n from '../../../translation/i18n';
import { setItem, getItem, removeItem, pushHashedEmail } from '../../helper/localstorage.helper';
import { updateUser as updateAuthUser, logOut } from "../../../redux/auth/authSlice";

// Backend sends this exact sentence (no error code) when a minor's account is
// still pending the parental consent email — translated via the matching key
// already present in translation/lang/*.js.
const PARENTAL_CONSENT_PENDING_MESSAGE =
    'We have not activated your account because we have not yet received the required confirmation email from your parents. Please ensure that this email is sent as soon as possible.';

export enum LatestDocumentStatus {
    Pending = 0,
    Approved = 1,
    Rejected = 2,
}

interface BankIDState {
    bankDetails: {
        id?: string;
        autoStartToken?: string;
    };
    qrCodeData: {
        id?: string;
        qrCode?: string;
        autoStartToken?: string;
    };
    isInitializing: boolean;
    isAuthenticating: boolean;
    isQRModalOpen: boolean;
    qrState: {
        isLoading: boolean;
        isAuthenticated: boolean;
        customMessage: string;
    };
    isAuthSuccessful: boolean;
    userData: any;
    error: string | null;
    message: string | null;
    sessionTimeout: boolean;
    needsReload: boolean;
    toggleReactivate: boolean;
    inactiveAccount: boolean;
    isIdentityVerified: boolean | null;
    canVerifyViaBankId: boolean | null;
    verificationStatusLoading: boolean;
    latestDocumentStatus: LatestDocumentStatus | null;
    rejectionReason: string | null;
    documents: Array<{
        documentId: number;
        documentName: string;
        status: number;
        rejectionReason: string | null;
        uploadedAt: string;
    }> | null;
    ssnMismatch: boolean;
    ssnUpdatePending: boolean;
    ssnUpdateRejected: boolean;
    ssnRejectionReason: string | null;
    nameMismatchType: 'firstName' | 'lastName' | null;
}

const initialState: BankIDState = {
    bankDetails: {},
    qrCodeData: {},
    isInitializing: false,
    isAuthenticating: false,
    isQRModalOpen: false,
    qrState: {
        isLoading: false,
        isAuthenticated: false,
        customMessage: "SCAN_QR_FROM_BANK_APP",
    },
    isAuthSuccessful: false,
    userData: null,
    error: null,
    message: null,
    sessionTimeout: false,
    needsReload: false,
    toggleReactivate: false,
    inactiveAccount: false,
    isIdentityVerified: null,
    canVerifyViaBankId: null,
    verificationStatusLoading: false,
    latestDocumentStatus: null,
    rejectionReason: null,
    documents: null,
    ssnMismatch: false,
    ssnUpdatePending: false,
    ssnUpdateRejected: false,
    ssnRejectionReason: null,
    nameMismatchType: null,
};

export const initializeBankIdVerificationDetails = createAsyncThunk(
    'bankid/initializeBankIdVerificationDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiCall.get('/bankid-verification/start-bankid');
            if (response?.status === 200) {
                return response.data.response;
            }
            throw new Error('Failed to initialize BankID verification details');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Failed to get bank verification details'
            );
        }
    }
);

export const initializeBankIdVerificationFlow = createAsyncThunk(
    'bankid/initializeBankIdVerificationFlow',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiCall.get('/bankid-verification/start-bankid');
            if (response?.status === 200) {
                return response.data.response;
            }
            throw new Error('Failed to initialize BankID verification flow');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Failed to generate verification QR code'
            );
        }
    }
);

export const initializeBankIDDetails = createAsyncThunk(
    'bankid/initializeBankIDDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiCall.get('/auth');
            if (response?.status === 200) {
                return response.data.response;
            }
            throw new Error('Failed to initialize BankID details');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Failed to get bank details'
            );
        }
    }
);

export const initializeBankIDFlow = createAsyncThunk(
    'bankid/initializeBankIDFlow',
    async (_, { rejectWithValue }) => {        
        try {
            const response = await apiCall.get('/auth');
            if (response?.status === 200) {
                return response.data.response;
            }
            throw new Error('Failed to initialize BankID flow');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Failed to generate QR code'
            );
        }
    }
);

export const checkBankIDAuthStatus = createAsyncThunk(
    'bankid/checkBankIDAuthStatus',
    async (
        { bankIdSession, mode, email , tduid }: { bankIdSession: string; mode: 'login' | 'signup'; email?: string , tduid?:string },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const base = `/auth/collect-response/qr-code/${bankIdSession}`;
            const endpoint =
                mode === "signup" && email
                    ? `${base}?email=${encodeURIComponent(email)}${tduid ? `&tduid=${encodeURIComponent(tduid)}` : ""
                    }`
                    : base;
            let response;

            if (mode === 'signup') {
                response = await apiCall.post(endpoint);
            } else {
                response = await apiCall.get(endpoint);
            }
            if (response.data.message === 'SESSION_TIMEOUT') {
                dispatch(setSessionTimeout(true));
                return response.data;
            }

            const bodyStatusCode = response?.data?.statusCode;

            // Terminal server-side error (e.g. 500 "Sequence contains more than
            // one element"). The HTTP layer may still return 200, so we must check
            // the body-level statusCode here before the isSuccess guard, otherwise
            // the fulfilled reducer's else branch only sets customMessage and the
            // polling loop never stops.
            if (bodyStatusCode && bodyStatusCode >= 500) {
                toast.error(i18n.t('BANKID_SERVER_ERROR') as string);
                return rejectWithValue(response.data?.message || 'BANKID_SERVER_ERROR');
            }

            const isSuccess =
                response?.status === 200 || response?.status === 201 ||
                bodyStatusCode === 200 || bodyStatusCode === 201;

            if (isSuccess) {
                const result = {
                    ...response.data,
                    mode,
                };

                if (result.response?.token && result.response?.userData) {
                if (mode === 'signup') {
                    window.dataLayer = window.dataLayer || [];
                    await pushHashedEmail(email);
                }
                    dispatch(updateAuthUser(result.response.userData));
                }

                return result;
            }

            throw new Error('Authentication check failed');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Authentication failed'
            );
        }
    }
);

export const checkBankIdVerificationAuthStatus = createAsyncThunk(
    'bankid/checkBankIdVerificationAuthStatus',
    async (
        { bankIdSession, mode, locale }: { bankIdSession: string; mode: 'login' | 'signup'; locale?: string },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const lang = locale || 'en-us';
            const response = await apiCall.get(`/bankid-verification/collect-bankid/${bankIdSession}?lang=${lang}`);

            if (response.data.message === 'SESSION_TIMEOUT') {
                dispatch(setSessionTimeout(true));
                return response.data;
            }

            // If backend signals an explicit error via statusCode in body
            const bodyStatusCode = response.data?.statusCode;
            if (bodyStatusCode && bodyStatusCode !== 200 && bodyStatusCode !== 201) {
                const errorMsg = response.data?.message || 'Authentication failed';
                if (errorMsg === 'BankID first name does not match your account.') {
                    dispatch(setNameMismatchType('firstName'));
                    return rejectWithValue(errorMsg);
                } else if (errorMsg === 'BankID last name does not match your account.') {
                    dispatch(setNameMismatchType('lastName'));
                    return rejectWithValue(errorMsg);
                } else if (errorMsg === 'BankID SSN does not match your account.') {
                    // Check for an existing SSN update request before opening the modal
                    try {
                        const ssnCheckRes = await apiCall.get('/ssn-update-request');
                        const rawCheck = ssnCheckRes.data?.response;
                        const firstRecord = Array.isArray(rawCheck) ? rawCheck[0] : rawCheck;
                        if (firstRecord?.status === 0) {
                            // Pending — admin is reviewing, show waiting card
                            dispatch(setSsnUpdatePending(true));
                        } else if (firstRecord?.status === 2) {
                            // Rejected — show SSN form with rejection banner + reason
                            dispatch(setSsnUpdateRejected(true));
                            dispatch(setSsnRejectionReason(firstRecord.rejectionReason || null));
                        }
                        // status 1 or no record → plain SSN input form
                    } catch {
                        // If the check fails, fall through to plain SSN form
                    }
                    dispatch(setSsnMismatch(true));
                } else {
                    toast.error(errorMsg);
                }
                return rejectWithValue(errorMsg);
            }

            if (response?.status === 200 || response?.status === 201) {
                const result = {
                    ...response.data,
                    mode,
                };

                if (result.response?.token && result.response?.userData) {
                    dispatch(updateAuthUser(result.response.userData));
                }

                toast.success(i18n.t('BANKID_VERIFICATION_COMPLETED') as string);

                // Re-fetch verification status after a successful collect
                dispatch(fetchBankIdVerificationStatus() as any);

                return result;
            }

            throw new Error('Authentication check failed');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Authentication failed'
            );
        }
    }
);

export const authenticateWithBankIDSameDevice = createAsyncThunk(
    'bankid/authenticateWithBankIDSameDevice',
    async (
        {
            mode,
            accountType,
            language,
            email,
            tduid
        }: {
            mode: 'login' | 'signup';
            accountType?: 'freelancer' | 'business';
            language?: string;
            email?: string;
            tduid?:string
        },
        { getState, rejectWithValue }
    ) => {
        try {
            const state = getState() as { bankidAuthFlow: BankIDState };
            const { bankDetails } = state.bankidAuthFlow;

            if (!bankDetails?.id || !bankDetails?.autoStartToken) {
                throw new Error('Bank details not available');
            }

            const baseUrl = (import.meta as any).env.VITE_API_URL || 'https://api-prod.invozio.com/api';
            const redirectParams = new URLSearchParams({
                lang: language || 'sv-se',
            });

            if (mode === 'signup') {
                redirectParams.set('signup', 'true');
                if (accountType) {
                    redirectParams.set('accountType', accountType === 'business' ? '2' : '1');
                }
                if (email) {
                    redirectParams.set('email', email);
                }
                if (tduid) {
                    redirectParams.set('tduid', tduid);
                }
            }

            const url1 = `${baseUrl}/auth/collect-response/${bankDetails.id}?${redirectParams.toString()}`;
            const encoded = encodeURIComponent(url1);
            const url = `https://app.bankid.com/?autostarttoken=${bankDetails.autoStartToken}&redirect=${encoded}`;

            // Persist session ID so mobile polling can resume after the browser
            // navigates away to BankID app and back (causing a page reload).
            setItem('bankidSameDeviceSessionId', bankDetails.id);

            window.open(url, '_blank');

            return { success: true, method: 'same-device' };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to open BankID app');
        }
    }
);
export const fetchBankIdVerificationStatus = createAsyncThunk(
    'bankid/fetchBankIdVerificationStatus',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiCall.get('/bankid-verification/status');
            if (response?.status === 200) {
                return response.data.response;
            }
            throw new Error('Failed to fetch verification status');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Failed to fetch verification status'
            );
        }
    }
);

const bankIDSlice = createSlice({
    name: 'bankid',
    initialState,
    reducers: {
        setQRModalOpen: (state, action) => {
            state.isQRModalOpen = action.payload;
        },

        setQRState: (state, action) => {
            state.qrState = { ...state.qrState, ...action.payload };
        },

        setSessionTimeout: (state, action) => {
            state.sessionTimeout = action.payload;
            if (action.payload) {
                state.qrState.customMessage = 'Your session has expired due to inactivity, Please try again';
                state.needsReload = true;
            }
        },

        setNeedsReload: (state, action) => {
            state.needsReload = action.payload;
            if (!action.payload) {
                state.qrState = {
                    isLoading: true,
                    isAuthenticated: false,
                    customMessage: "SCAN_QR_FROM_BANK_APP",
                };
                state.sessionTimeout = false;
            }
        },

        setAuthSuccessful: (state, action) => {
            state.isAuthSuccessful = action.payload;
        },

        resetBankIDFlow: (state) => {
            state.qrCodeData = {};
            state.qrState = {
                isLoading: false,
                isAuthenticated: false,
                customMessage: "SCAN_QR_FROM_BANK_APP",
            };
            state.isQRModalOpen = false;
            state.isAuthSuccessful = false;
            state.error = null;
            state.message = null;
            state.sessionTimeout = false;
            state.needsReload = false;
            state.nameMismatchType = null;
        },

        clearQRData: (state) => {
            state.qrCodeData = {};
            state.qrState = {
                isLoading: true,
                isAuthenticated: false,
                customMessage: 'Scan QR from your bank\'s mobile application',
            };
            state.sessionTimeout = false;
            state.needsReload = false;
            state.error = null;
        },

        resetBankIDState: () => initialState,

        clearError: (state) => {
            state.error = null;
        },

        setCustomMessage: (state, action) => {
            state.qrState.customMessage = action.payload;
        },

        // Account reactivation actions
        setToggleReactivate: (state, action) => {
            state.toggleReactivate = action.payload;
        },

        setInactiveAccount: (state, action) => {
            state.inactiveAccount = action.payload;
        },

        setSsnMismatch: (state, action) => {
            state.ssnMismatch = action.payload;
            if (!action.payload) {
                state.ssnUpdatePending = false;
                state.ssnUpdateRejected = false;
                state.ssnRejectionReason = null;
            }
        },

        setSsnUpdatePending: (state, action) => {
            state.ssnUpdatePending = action.payload;
        },

        setSsnUpdateRejected: (state, action) => {
            state.ssnUpdateRejected = action.payload;
        },

        setSsnRejectionReason: (state, action) => {
            state.ssnRejectionReason = action.payload;
        },

        setNameMismatchType: (state, action) => {
            state.nameMismatchType = action.payload;
        },

        // Restores a same-device BankID session after a mobile page reload so
        // the polling mechanism can resume and save isLoginWithBankId.
        restoreSameDeviceSession: (state, action: { payload: string }) => {
            state.qrCodeData = { id: action.payload };
        },

        // Clear state on signout
        clearBankIDStateOnSignout: () => initialState,
        handleReactivationSuccess: (state) => {
            state.inactiveAccount = false;
            state.toggleReactivate = false;
            state.error = null;
            state.qrCodeData = {};
            state.bankDetails = {};
            state.qrState = {
                isLoading: false,
                isAuthenticated: false,
                customMessage: "SCAN_QR_FROM_BANK_APP",
            };
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(initializeBankIDDetails.pending, (state) => {
                state.isInitializing = true;
                state.error = null;
                // Only clear QR session if there is no pending same-device mobile
                // session — clearing it would stop the resumed polling.
                if (!getItem('bankidSameDeviceSessionId')) {
                    state.qrCodeData = {};
                }
            })
            .addCase(initializeBankIDDetails.fulfilled, (state, action) => {
                state.isInitializing = false;
                state.bankDetails = action.payload;
            })
            .addCase(initializeBankIDDetails.rejected, (state, action) => {
                state.isInitializing = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(initializeBankIdVerificationDetails.pending, (state) => {
                state.isInitializing = true;
                state.error = null;
                state.qrCodeData = {};
            })
            .addCase(initializeBankIdVerificationDetails.fulfilled, (state, action) => {
                state.isInitializing = false;
                state.bankDetails = action.payload;
            })
            .addCase(initializeBankIdVerificationDetails.rejected, (state, action) => {
                state.isInitializing = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(initializeBankIDFlow.pending, (state) => {
                state.qrState.isLoading = true;
                state.error = null;
            })
            .addCase(initializeBankIDFlow.fulfilled, (state, action) => {
                state.qrState.isLoading = false;
                state.qrCodeData = action.payload;
            })
            .addCase(initializeBankIDFlow.rejected, (state, action) => {
                state.qrState.isLoading = false;
                state.error = action.payload as string;
                state.qrState.customMessage = action.payload as string || 'Failed to generate QR code';
            });

        builder
            .addCase(initializeBankIdVerificationFlow.pending, (state) => {
                state.qrState.isLoading = true;
                state.error = null;
            })
            .addCase(initializeBankIdVerificationFlow.fulfilled, (state, action) => {
                state.qrState.isLoading = false;
                state.qrCodeData = action.payload;
            })
            .addCase(initializeBankIdVerificationFlow.rejected, (state, action) => {
                state.qrState.isLoading = false;
                state.error = action.payload as string;
                state.qrState.customMessage = action.payload as string || 'Failed to generate verification QR code';
            });

        builder
            .addCase(checkBankIDAuthStatus.pending, (state) => {
                state.isAuthenticating = true;
            })
            .addCase(checkBankIDAuthStatus.fulfilled, (state, action) => {
                state.isAuthenticating = false;
                if (!action?.payload) return;
                const { response, message, mode } = action.payload;
                if (response?.token) {
                    state.qrState.isAuthenticated = true;
                    state.isAuthSuccessful = true;
                    state.userData = response;
                    setItem('user', JSON.stringify(response));
                    removeItem('bankidSameDeviceSessionId');

                } else {
                    // Check SESSION_TIMEOUT first — its response can also have
                    // isLoginWithBankId=false with no token, which would otherwise
                    // incorrectly trigger the isAccountNotFound branch.
                    if (message === 'SESSION_TIMEOUT') {
                        state.sessionTimeout = true;
                        state.needsReload = true;
                        state.userData = initialState;
                        state.qrState.customMessage = 'BANKID_SESSION_EXPIRED';
                        toast.error(i18n.t('BANKID_SESSION_EXPIRED') as string);
                    } else if (message === PARENTAL_CONSENT_PENDING_MESSAGE) {
                        state.error = message;
                        state.isQRModalOpen = false;
                        state.needsReload = true;
                        toast.error(i18n.t(PARENTAL_CONSENT_PENDING_MESSAGE) as string);
                    } else {
                    // Check known specific messages first — their responses can also
                    // have isLoginWithBankId=false with no token, which would otherwise
                    // incorrectly trigger the isAccountNotFound branch.
                    if (mode === 'signup' && message === 'User already registerd') {
                        toast.error(i18n.t('BANKID_ALREADY_REGISTERED') as string);
                        state.isQRModalOpen = false;
                        state.error = message;
                    } else if (message === 'account_deactivated' || message === 'inactive_account') {
                        if (response?.userData) {
                            state.userData = response.userData;
                        } else if (response) {
                            state.userData = response;
                        }
                        state.isQRModalOpen = false;
                        state.needsReload = true;
                        state.error = message;
                        state.inactiveAccount = message === 'inactive_account';
                        state.toggleReactivate = true;
                    } else if (typeof message === 'string' && message.toLowerCase().includes('could not be matched with the personal number')) {
                        // BankID personal-number mismatch — user authenticated via BankID but the
                        // SSN does not match the one on their account. Direct them to email login.
                        state.error = message;
                        state.isQRModalOpen = false;
                        toast.error(i18n.t('BANKID_PERSONAL_NUMBER_MISMATCH') as string);
                    } else {
                    // Use the reliable boolean signal from the API — avoids brittle
                    // string-matching that breaks on Unicode apostrophe variants.
                    const isAccountNotFound =
                        (response?.isLoginWithBankId === false && !response?.token) ||
                        (typeof message === 'string' && message.toLowerCase().includes("couldn't find an account"));

                    if (isAccountNotFound) {
                        state.error = message || 'BANKID_SSN_NOT_FOUND'; // stops polling guard (!!error)
                        state.isQRModalOpen = false;
                        toast.error(i18n.t('BANKID_SSN_NOT_FOUND') as string);
                    } else {
                        state.qrState.customMessage = message || 'Please check your BankID app';
                    }
                    }
                    }
                }
            })
            .addCase(checkBankIDAuthStatus.rejected, (state, action) => {
                state.isAuthenticating = false;
                state.error = action.payload as string;
                state.qrState.customMessage = action.payload as string || 'Authentication failed';
                removeItem('bankidSameDeviceSessionId');
            });

        builder
            .addCase(checkBankIdVerificationAuthStatus.pending, (state) => {
                state.isAuthenticating = true;
            })
            .addCase(checkBankIdVerificationAuthStatus.fulfilled, (state, action) => {
                state.isAuthenticating = false;
                if (!action?.payload) return;
                const { response, message, mode } = action.payload;
                if (response?.token) {
                    state.qrState.isAuthenticated = true;
                    state.isAuthSuccessful = true;
                    state.userData = response;
                    setItem('user', JSON.stringify(response));
                } else {
                    if (message === 'SESSION_TIMEOUT') {
                        state.sessionTimeout = true;
                        state.needsReload = true;
                        state.userData = initialState;
                        state.qrState.customMessage = 'BANKID_SESSION_EXPIRED';
                        toast.error(i18n.t('BANKID_SESSION_EXPIRED') as string);
                    } else if (message === PARENTAL_CONSENT_PENDING_MESSAGE) {
                        state.error = message;
                        state.needsReload = true;
                        toast.error(i18n.t(PARENTAL_CONSENT_PENDING_MESSAGE) as string);
                    } else {
                        state.qrState.customMessage = message || 'Please check your BankID app';
                    }
                }
            })
            .addCase(checkBankIdVerificationAuthStatus.rejected, (state, action) => {
                state.isAuthenticating = false;
                state.error = action.payload as string;
                state.qrState.customMessage = action.payload as string || 'Authentication failed';
            });

        builder
            .addCase(authenticateWithBankIDSameDevice.pending, (state) => {
                state.isInitializing = true;
            })
            .addCase(authenticateWithBankIDSameDevice.fulfilled, (state) => {
                state.isInitializing = false;
                // Seed qrCodeData.id with the bankDetails session id so the
                // existing polling mechanism can detect auth completion and
                // save isLoginWithBankId from the API response.
                if (state.bankDetails?.id) {
                    state.qrCodeData = { ...state.qrCodeData, id: state.bankDetails.id };
                }
            })
            .addCase(authenticateWithBankIDSameDevice.rejected, (state, action) => {
                state.isInitializing = false;
                state.error = action.payload as string;
                toast.error(action.payload as string);
            });

        builder
            .addCase(fetchBankIdVerificationStatus.pending, (state) => {
                state.verificationStatusLoading = true;
            })
            .addCase(fetchBankIdVerificationStatus.fulfilled, (state, action) => {
                state.verificationStatusLoading = false;
                state.isIdentityVerified = action.payload?.isIdentityVerified ?? null;
                state.canVerifyViaBankId = action.payload?.canVerifyViaBankId ?? null;
                state.latestDocumentStatus = action.payload?.latestDocumentStatus ?? null;
                state.rejectionReason = action.payload?.rejectionReason ?? null;
                state.documents = action.payload?.documents ?? null;
            })
            .addCase(fetchBankIdVerificationStatus.rejected, (state) => {
                state.verificationStatusLoading = false;
            });

        builder
            .addCase(logOut, () => initialState);
    },
});

export const {
    setQRModalOpen,
    setQRState,
    setSessionTimeout,
    setNeedsReload,
    setAuthSuccessful,
    resetBankIDFlow,
    clearQRData,
    resetBankIDState,
    clearError,
    setCustomMessage,
    setToggleReactivate,
    setInactiveAccount,
    clearBankIDStateOnSignout,
    handleReactivationSuccess,
    setSsnMismatch,
    setSsnUpdatePending,
    setSsnUpdateRejected,
    setSsnRejectionReason,
    setNameMismatchType,
    restoreSameDeviceSession,
} = bankIDSlice.actions;

export const submitSsnUpdate = createAsyncThunk(
    'bankid/submitSsnUpdate',
    async ({ newSsn }: { newSsn: string }, { rejectWithValue, dispatch }) => {
        try {
            const postResponse = await apiCall.post('/ssn-update-request', { newSsn });
            if (postResponse?.status === 200 || postResponse?.status === 201) {
                // Check if the body carries a non-success statusCode (API returns HTTP 200 with error body)
                const bodyStatusCode = postResponse.data?.statusCode;
                if (bodyStatusCode === 409) {
                    return rejectWithValue({ isDuplicate: true });
                }
                if (bodyStatusCode === 400) {
                    return rejectWithValue({ isSameSsn: true });
                }
                // POST succeeded — call GET /ssn-update-request to check status
                const getResponse = await apiCall.get('/ssn-update-request');
                if (getResponse?.status === 200 || getResponse?.status === 201) {
                    const raw = getResponse.data?.response;
                    const record = Array.isArray(raw) ? raw[0] : raw;
                    if (record?.status === 0) {
                        // Pending — show pending card, keep modal open
                        dispatch(setSsnUpdatePending(true));
                    } else if (record?.status === 1) {
                        // status 1 — keep modal open with plain SSN form for re-entry
                    } else if (record?.status === 2) {
                        // Rejected — show rejection banner, keep modal open
                        dispatch(setSsnUpdateRejected(true));
                        dispatch(setSsnRejectionReason(record.rejectionReason || null));
                    } else {
                        // No record / other terminal status — close modal
                        dispatch(setSsnMismatch(false));
                    }
                }
                return getResponse.data;
            }
            throw new Error('SSN update failed');
        } catch (error: any) {
            if (
                error?.response?.status === 409 &&
                error?.response?.data?.message === 'The SSN you entered is already associated with another account. Please check and try again.'
            ) {
                return rejectWithValue({ isDuplicate: true });
            }
            return rejectWithValue(
                error?.response?.data?.message || 'SSN update failed'
            );
        }
    }
);

export default bankIDSlice.reducer;
